import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApolloQueryResult, FetchResult } from '@apollo/client/core';
import {
  ChannelNotification,
  ChannelNotificationFilterAttributes,
  GetChannelNotificationGQL,
  GetChannelNotificationQuery,
  GetChannelNotificationsGQL,
  GetChannelNotificationsQuery,
  MarkNotificationsAsReadGQL,
  MarkNotificationsAsUnreadGQL,
  MarkNotificationsAsUnreadMutation,
  ArchiveNotificationsGQL,
  ArchiveNotificationsMutation,
  MarkNotificationsAsReadMutation,
  NotificationRulesQuery,
  NotificationRule,
  NotificationRulesGQL,
  SubscribeToNotificationRulesGQL,
  NotificationSlugEnum,
  GetTotalChannelNotificationsCountGQL,
  GetUnreadChannelNotificationsCountGQL,
  GetUnreadChannelNotificationsCountQuery,
  GetTotalChannelNotificationsCountQuery,
  NotificationRuleSeverityEnum,
} from '@app/generated/graphql';
import { Apollo } from 'apollo-angular';

/** The result of a paginated list of channel notifications. */
export interface PaginatedNotificationsResult {
  nodes: ChannelNotification[];
  totalCount: number;
  pageInfo: {
    endCursor: string | null;
    hasNextPage: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsDataService {
  // dependencies
  private _apollo = inject(Apollo);
  private _markNotificationsAsReadGQL = inject(MarkNotificationsAsReadGQL);
  private _archiveNotificationsGQL = inject(ArchiveNotificationsGQL);
  private _markNotificationsAsUnreadGQL = inject(MarkNotificationsAsUnreadGQL);
  private _getChannelNotificationsGQL = inject(GetChannelNotificationsGQL);
  private _getChannelNotificationGQL = inject(GetChannelNotificationGQL);
  private _getNotificationRulesGQL = inject(NotificationRulesGQL);
  private _subscribeToNotificationRulesGQL = inject(
    SubscribeToNotificationRulesGQL,
  );
  private _getTotalChannelNotificationsCountGQL = inject(
    GetTotalChannelNotificationsCountGQL,
  );
  private _getUnreadChannelNotificationsCountGQL = inject(
    GetUnreadChannelNotificationsCountGQL,
  );

  /** Fetches a list of channel notifications from the backend.
   * @param filterAttributes - The filter attributes to apply to the query.
   * @param first - The number of notifications to fetch.
   * @param after - The cursor to use to fetch the next page of notifications.
   * @returns An observable of the paginated notifications result.
   */
  public getNotifications$(
    filterAttributes: ChannelNotificationFilterAttributes,
    first: number = 5,
    after?: string | null,
  ): Observable<PaginatedNotificationsResult> {
    return this._apollo
      .query({
        query: this._getChannelNotificationsGQL.document,
        variables: { filterAttributes, first, after },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map(
          (
            res: ApolloQueryResult<GetChannelNotificationsQuery>,
          ): PaginatedNotificationsResult => {
            const connection = res.data?.channelNotifications;
            return {
              nodes: connection?.nodes ?? [],
              pageInfo: {
                endCursor: connection?.pageInfo?.endCursor ?? null,
                hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
              },
              totalCount: connection?.totalCount ?? 0,
            };
          },
        ),
      );
  }

  /** Fetches a list of notification rules from the backend, with an optional notification slug filter.
   * @param notificationSlugs - The notification slugs to fetch the rules for.
   * @returns An observable of the notification rules.
   */
  public getNotificationRules$(
    notificationSlugs: NotificationSlugEnum[],
  ): Observable<NotificationRule[]> {
    return this._apollo
      .query({
        query: this._getNotificationRulesGQL.document,
        variables: { notificationSlugs },
      })
      .pipe(
        map(
          (res: ApolloQueryResult<NotificationRulesQuery>) =>
            res.data?.notificationRules ?? [],
        ),
      );
  }

  /** Marks a list of channel notifications as unread.
   * @param ids - The ids of the notifications to mark as unread.
   * @returns An observable of the mutation result.
   */
  public markNotificationsAsUnread$(
    ids: string[],
  ): Observable<FetchResult<MarkNotificationsAsUnreadMutation>> {
    return this._apollo
      .mutate<MarkNotificationsAsUnreadMutation>({
        mutation: this._markNotificationsAsUnreadGQL.document,
        variables: { ids },
      })
      .pipe(map((res: FetchResult<MarkNotificationsAsUnreadMutation>) => res));
  }

  /** Archives a list of channel notifications.
   * @param ids - The ids of the notifications to archive.
   * @returns An observable of the mutation result.
   */
  public archiveNotifications$(
    ids: string[],
  ): Observable<FetchResult<ArchiveNotificationsMutation>> {
    return this._apollo
      .mutate<ArchiveNotificationsMutation>({
        mutation: this._archiveNotificationsGQL.document,
        variables: { ids },
      })
      .pipe(map((res: FetchResult<ArchiveNotificationsMutation>) => res));
  }

  /** Fetches a single channel notification from the backend.
   * @param id - The id of the notification to fetch.
   * @returns An observable of the notification.
   */
  public getNotification$(
    id: string | number,
  ): Observable<ChannelNotification> {
    return this._apollo
      .query({
        query: this._getChannelNotificationGQL.document,
        fetchPolicy: 'no-cache',
        variables: { id },
      })
      .pipe(
        map(
          (res: ApolloQueryResult<GetChannelNotificationQuery>) =>
            res.data?.channelNotificationById,
        ),
      );
  }

  /** Marks a list of channel notifications as read.
   * @param ids - The ids of the notifications to mark as read.
   * @returns An observable of the mutation result.
   */
  public markNotificationsAsRead$(
    ids: string[],
  ): Observable<FetchResult<MarkNotificationsAsReadMutation>> {
    return this._apollo
      .mutate<MarkNotificationsAsReadMutation>({
        mutation: this._markNotificationsAsReadGQL.document,
        variables: { ids },
      })
      .pipe(map((res: FetchResult<MarkNotificationsAsReadMutation>) => res));
  }

  /** Fetches the total number of channel notifications for a given severity.
   * Useful for determining whether or not a popover or drawer should be rendered in the notification/alert inboxes
   * @param severity - The severity of the notifications to fetch the count for.
   * @returns An observable of the total number of notifications.
   */
  public getTotalChannelNotificationsCount$(
    severity: NotificationRuleSeverityEnum,
  ): Observable<number> {
    return this._apollo
      .query({
        query: this._getTotalChannelNotificationsCountGQL.document,
        variables: { severity },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map(
          (res: ApolloQueryResult<GetTotalChannelNotificationsCountQuery>) =>
            res.data?.channelNotificationsCount ?? 0,
        ),
      );
  }

  /** Fetches the number of unread channel notifications for a given severity.
   * Useful for displaying the number of unread notifications in the notifications/alerts inbox.
   * @param severity - The severity of the notifications to fetch the count for.
   * @returns An observable of the number of unread notifications.
   */
  public getUnreadChannelNotificationsCount$(
    severity: NotificationRuleSeverityEnum,
  ): Observable<number> {
    return this._apollo
      .query({
        query: this._getUnreadChannelNotificationsCountGQL.document,
        variables: { severity },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map(
          (res: ApolloQueryResult<GetUnreadChannelNotificationsCountQuery>) =>
            res.data?.unreadChannelNotificationsCount ?? 0,
        ),
      );
  }
}
